import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from './use-auth';

/**
 * Guarda de rota. Conveniência de navegação, **não** controle de acesso: o backend nega de
 * novo, e é ele quem decide.
 */
export function RequireAuth({
  children,
  permission,
  scope = 'organization',
}: {
  children: ReactNode;
  permission?: string;
  /**
   * Nível da tela (ADR-027). A conta de plataforma não usa telas de empresa, e vice-versa: cada
   * uma é mandada para a sua casa. O backend recusa do mesmo jeito.
   */
  scope?: 'organization' | 'platform';
}) {
  const { user, isRestoring, can, isPlatform } = useAuth();
  const location = useLocation();

  // Durante a restauração não se decide nada: redirecionar aqui jogaria para o login quem
  // está autenticado e só recarregou a página.
  if (isRestoring) {
    return (
      <div className="space-y-4 p-6" aria-busy="true" aria-live="polite">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!user) {
    // A busca vai junto: a volta de uma autorização OAuth traz `code` e `state` na URL.
    return (
      <Navigate to="/entrar" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  if (scope === 'platform' && !isPlatform) return <Navigate to="/" replace />;
  if (scope === 'organization' && isPlatform) return <Navigate to="/plataforma" replace />;

  if (permission !== undefined && !can(permission)) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-foreground">Sem permissão</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sua conta não tem acesso a esta área. Fale com um administrador da empresa.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
